import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Progress,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  CalendarIcon,
  EditIcon,
  FileTextIcon,
  PackageIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { formatDate } from "@/shared/lib/date-time";
import { CancelPackageDialog } from "@/shared/ui/CancelPackageDialog";
import type { ActivePackageDTO } from "../../domain/repo/active-package.dto";
import { useCancelStudentPackage } from "../api/queries";
import { EditPackageSelectedClassesDialog } from "./EditPackageSelectedClassesDialog";

type ActivePackageSectionProps = {
  studentId: string;
  packages: NonNullable<ActivePackageDTO>[];
};

export function ActivePackageSection({
  studentId: _studentId,
  packages,
}: ActivePackageSectionProps) {
  const _nav = useNavigate();

  if (packages.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileTextIcon className="size-5 text-destructive" />
              <CardTitle>ລາຍລະອຽດແພັກເກດທີ່ໃຊ້ງານ</CardTitle>
            </div>
            <Button
              onClick={() => {
                _nav({
                  to: "/app/sell",
                  search: { studentId: _studentId, type: "package" },
                });
              }}
            >
              <ShoppingCartIcon className="size-4" />
              ຂາຍແພັກເກດໃໝ່
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <PackageIcon className="size-12 text-muted-foreground" />
              </EmptyMedia>
              <EmptyTitle>ບໍ່ມີແພັກເກດທີ່ໃຊ້ງານ</EmptyTitle>
              <EmptyDescription>
                ນັກຮຽນຍັງບໍ່ມີແພັກເກດທີ່ໃຊ້ງານຢູ່ ກະລຸນາຂາຍແພັກເກດໃໝ່ໃຫ້ນັກຮຽນ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileTextIcon className="size-5 text-destructive" />
              <CardTitle>ແພັກເກດຄລາດສ໌ທີ່ໃຊ້ງານ ({packages.length})</CardTitle>
            </div>
            <Button
              onClick={() => {
                _nav({
                  to: "/app/sell",
                  search: { studentId: _studentId, type: "package" },
                });
              }}
            >
              <ShoppingCartIcon className="size-4" />
              ຂາຍແພັກເກດໃໝ່
            </Button>
          </div>
        </CardHeader>
      </Card>

      {packages.map((activePackage) => (
        <ActivePackageCard
          key={activePackage.id}
          studentId={_studentId}
          activePackage={activePackage}
        />
      ))}
    </div>
  );
}

function ActivePackageCard({
  studentId: _studentId,
  activePackage,
}: {
  studentId: string;
  activePackage: NonNullable<ActivePackageDTO>;
}) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [editClassesDialogOpen, setEditClassesDialogOpen] = useState(false);
  const cancelPackage = useCancelStudentPackage(_studentId);
  const usedSessions = activePackage.usedSessions;
  const totalSessions = activePackage.totalSessions;
  const usedPercentage =
    totalSessions > 0 ? Math.round((usedSessions / totalSessions) * 100) : 0;

  // Check if package has selected classes (multiple classes)
  const hasSelectedClasses =
    activePackage.selectedClasses && activePackage.selectedClasses.length > 0;

  return (
    <div className="space-y-6">
      <CancelPackageDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        packageLabel={activePackage.templateName}
        onConfirm={async (cancelReason) => {
          await cancelPackage.mutateAsync({
            packageId: activePackage.id,
            cancelReason,
          });
        }}
      />
      {/* Two Panels: Session Usage and Package Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left Panel: Session Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-sm uppercase">
              ການໃຊ້ງານຄັ້ງ
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {hasSelectedClasses ? (
              /* Multiple Progress Bars - One per Class */
              <div className="space-y-4">
                {activePackage.selectedClasses.map((c, index) => {
                  const classUsedSessions =
                    c.allocatedSessions - c.remainingSessions;
                  const classUsedPercentage =
                    c.allocatedSessions > 0
                      ? Math.round(
                          (classUsedSessions / c.allocatedSessions) * 100,
                        )
                      : 0;

                  return (
                    <div key={c.id} className="space-y-2">
                      {index > 0 && <div className="border-t pt-4" />}
                      <div className="text-center">
                        <h4 className="font-semibold text-sm">
                          {c.classTypeName} - {c.levelName}
                        </h4>
                      </div>
                      {/* Usage Summary for this class */}
                      <div className="text-center text-sm">
                        <span className="font-semibold">
                          ໃຊ້ແລ້ວ: {classUsedSessions} ຄັ້ງ
                        </span>{" "}
                        <span className="text-muted-foreground">
                          ທັງໝົດ: {c.allocatedSessions} ຄັ້ງ
                        </span>
                      </div>
                      {/* Progress Bar */}
                      <Progress value={classUsedPercentage} className="h-2" />
                    </div>
                  );
                })}
                {/* Overall Summary */}
                <div className="mt-4 rounded-lg border bg-muted/50 p-3">
                  <div className="text-center text-sm">
                    <span className="font-semibold">
                      ລວມໃຊ້ແລ້ວ: {usedSessions} ຄັ້ງ
                    </span>{" "}
                    <span className="text-muted-foreground">
                      ລວມທັງໝົດ: {totalSessions} ຄັ້ງ
                    </span>
                  </div>
                  <Progress value={usedPercentage} className="mt-2 h-2" />
                </div>
              </div>
            ) : (
              /* Single Progress Bar - Overall (backward compatibility) */
              <div className="space-y-2">
                <div className="text-center text-sm">
                  <span className="font-semibold">
                    ໃຊ້ແລ້ວ: {usedSessions} ຄັ້ງ
                  </span>{" "}
                  <span className="text-muted-foreground">
                    ທັງໝົດ: {totalSessions} ຄັ້ງ
                  </span>
                </div>
                {/* Progress Bar */}
                <Progress value={usedPercentage} className="h-2" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Panel: Package Details */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {activePackage.templateName}
              </CardTitle>
              <Badge variant="default">ໃຊ້ງານ</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Package ID: #{activePackage.id.slice(0, 8).toUpperCase()}
            </p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setCancelDialogOpen(true)}
              >
                <XIcon className="mr-2 size-4" />
                ຍົກເລີກແພັກເກດ
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີ່ເລີ່ມຕົ້ນ
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.startDate)}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີ່ໝົດອາຍຸ
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.expireDate)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <div className="font-medium text-muted-foreground text-xs uppercase">
                    ວັນທີ່ຊື້
                  </div>
                  <div className="flex items-center gap-2">
                    <ShoppingBagIcon className="size-4 text-destructive" />
                    <span className="font-semibold">
                      {formatDate(activePackage.purchasedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {activePackage.selectedClasses &&
              activePackage.selectedClasses.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="font-medium text-muted-foreground text-xs uppercase">
                      ຄລາດສ໌ທີ່ເລືອກ
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditClassesDialogOpen(true)}
                    >
                      <EditIcon className="mr-2 size-4" />
                      ແກ້ໄຂຄລາດສ໌ທີ່ເລືອກ
                    </Button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {activePackage.selectedClasses.map((c) => (
                      <div
                        key={`${c.classTypeName}-${c.levelName}-${c.id}`}
                        className="flex items-center justify-between rounded bg-muted px-3 py-2"
                      >
                        <span className="font-medium text-sm">
                          {c.classTypeName} - {c.levelName}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          {c.remainingSessions} / {c.allocatedSessions} ຄັ້ງ
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Loyalty Discount Alert */}
            {/* <div className="mt-6 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-3">
                <InfoIcon className="mt-0.5 size-5 text-destructive" />
                <div className="flex-1">
                  <p className="text-sm">
                    ແພັກເກດນີ້ມີສິດໄດ້ຮັບສ່ວນຫຼຸດ 10% ສຳລັບການຕໍ່ອາຍຸກ່ອນໝົດອາຍຸ.
                  </p>
                  <Button
                    variant="link"
                    className="mt-2 h-auto p-0 text-destructive"
                    onClick={() => {
                      // TODO: Navigate to package details
                    }}
                  >
                    ເບິ່ງລາຍລະອຽດ
                  </Button>
                </div>
              </div>
            </div> */}
          </CardContent>
        </Card>
      </div>

      <EditPackageSelectedClassesDialog
        open={editClassesDialogOpen}
        onOpenChange={setEditClassesDialogOpen}
        studentId={_studentId}
        packageId={activePackage.id}
        templateId={activePackage.templateId}
        initialSelectedRuleIds={activePackage.selectedClasses
          .map((c) => c.allowedRuleId)
          .filter((x): x is string => !!x)}
        initialClassSessions={activePackage.selectedClasses
          .filter((c) => c.allowedRuleId)
          .map((c) => ({
            ruleId: c.allowedRuleId as string,
            sessions: c.allocatedSessions,
          }))}
        initialSelectedClasses={activePackage.selectedClasses
          .filter((c) => c.allowedRuleId)
          .map((c) => ({
            allowedRuleId: c.allowedRuleId as string,
            classTypeName: c.classTypeName,
            levelName: c.levelName,
            allocatedSessions: c.allocatedSessions,
            remainingSessions: c.remainingSessions,
          }))}
      />
    </div>
  );
}
